import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { TextField, SelectField } from "@/components/common";
import { USER_ROLE, OFFICE_TYPE, fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Download, BadgeCheck, MapPinned, ShieldCheck, Copy, ScrollText, Mail, Activity, RefreshCw, ShieldAlert, Ban, CheckCircle2, AlertTriangle, HelpCircle, Gauge, Lock, Database, DownloadCloud, PlayCircle } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FORMULAS = [
  ["Estimated Equity", "Market Value − Loan Balance − Known Liens"],
  ["Flip Net Profit", "ARV − Purchase − Repairs − Closing − Holding − Selling"],
  ["Cash Required", "Down Payment + Arrearages + Seller Consideration + Repairs + Closing + Reserves"],
  ["Monthly Cash Flow", "Rent − Debt Service − Taxes − Insurance − HOA − Mgmt − Maintenance − Vacancy"],
  ["Cash-on-Cash", "Annual Cash Flow ÷ Total Cash Invested"],
  ["DSCR", "Annual NOI ÷ Annual Debt Service"],
  ["Max Allowable Offer", "ARV × Target % − Repairs − Acquisition Fee"],
];

export default function Settings() {
  const { user: me } = useAuth();
  const isAdmin = ["System Owner", "Administrator"].includes(me?.role);
  const [company, setCompany] = useState({});
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [nu, setNu] = useState({ role: "Read Only" });
  const [invOpen, setInvOpen] = useState(false);
  const [inv, setInv] = useState({ role: "Read Only" });
  const [inviteLink, setInviteLink] = useState("");
  const [invError, setInvError] = useState("");
  const setC = (k) => (v) => setCompany((p) => ({ ...p, [k]: v }));
  const setNU = (k) => (v) => setNu((p) => ({ ...p, [k]: v }));

  const loadUsers = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { api.get("/company").then((r) => setCompany(r.data)); loadUsers(); }, []);

  const saveCompany = async () => { await api.put("/company", company); toast.success("Company settings saved"); };
  const addUser = async () => {
    try { await api.post("/users", nu); toast.success("User created"); setOpen(false); setNu({ role: "Read Only" }); loadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const revokeSeat = async (u) => {
    try { await api.post(`/users/${u.id}/deactivate`); toast.success(`Seat revoked for ${u.first_name} ${u.last_name}`); loadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to revoke seat"); }
  };
  const reactivateSeat = async (u) => {
    try { await api.post(`/users/${u.id}/reactivate`); toast.success(`Seat restored for ${u.first_name} ${u.last_name}`); loadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to reactivate seat"); }
  };
  const setINV = (k) => (v) => setInv((p) => ({ ...p, [k]: v }));
  const sendInvite = async () => {
    setInvError("");
    try {
      const { data } = await api.post("/users/invite", inv);
      setInviteLink(data.invite_link);
      toast.success(`Invite created for ${data.email}`);
      loadUsers();
    } catch (e) { setInvError(e.response?.data?.detail || "Failed to create invite"); }
  };
  const copyInvite = () => { navigator.clipboard?.writeText(inviteLink); toast.success("Invite link copied"); };
  const [resetInfo, setResetInfo] = useState(null);
  const [reset2faUser, setReset2faUser] = useState(null);
  const [reauth, setReauth] = useState({ password: "", code: "" });
  const [reset2faBusy, setReset2faBusy] = useState(false);
  const doReset2fa = async () => {
    setReset2faBusy(true);
    try {
      await api.post(`/auth/2fa/admin-reset/${reset2faUser.id}`, { password: reauth.password, code: reauth.code });
      toast.success(`${reset2faUser.first_name || reset2faUser.email} will set up 2FA again on next sign-in`);
      setReset2faUser(null); setReauth({ password: "", code: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reset 2FA"); }
    finally { setReset2faBusy(false); }
  };
  const genResetLink = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-link`);
      setResetInfo({ ...data, name: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate a reset link"); }
  };
  const [forceUser, setForceUser] = useState(null);
  const [forceReason, setForceReason] = useState("");
  const [forceBusy, setForceBusy] = useState(false);
  const doForceReset = async () => {
    setForceBusy(true);
    try {
      await api.post(`/users/${forceUser.id}/force-reset`, { reason: forceReason });
      toast.success(`${forceUser.first_name || forceUser.email} must reset their password on next sign-in`);
      setForceUser(null); setForceReason(""); loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not force a reset"); }
    finally { setForceBusy(false); }
  };
  const deleteMember = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success(`${u.first_name || u.email} removed`); loadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to remove member"); }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Company, team, formulas & defaults" />
      <div className="p-4 sm:p-6 lg:p-8">
        <Tabs defaultValue="company">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="w-max"><TabsTrigger value="company" data-testid="tab-company">Company</TabsTrigger><TabsTrigger value="plan" data-testid="tab-plan">Plan &amp; Billing</TabsTrigger><TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>{isAdmin && <TabsTrigger value="usage" data-testid="tab-usage">Usage &amp; Limits</TabsTrigger>}<TabsTrigger value="users" data-testid="tab-users">Users &amp; Roles</TabsTrigger>{isAdmin && <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>}<TabsTrigger value="county" data-testid="tab-county">County Offices</TabsTrigger><TabsTrigger value="formulas" data-testid="tab-formulas">Formulas</TabsTrigger></TabsList>
          </div>

          <TabsContent value="company" className="mt-5">
            <div className="bg-card border border-border rounded-md p-6 max-w-3xl grid sm:grid-cols-2 gap-4">
              <TextField label="Company Name" value={company.company_name} onChange={setC("company_name")} testid="co-name" />
              <TextField label="Legal Name" value={company.legal_name} onChange={setC("legal_name")} testid="co-legal" />
              <TextField label="EIN (Last 4)" value={company.ein_last_four} onChange={setC("ein_last_four")} testid="co-ein" />
              <TextField label="Phone" value={company.phone} onChange={setC("phone")} testid="co-phone" />
              <TextField label="Email" value={company.email} onChange={setC("email")} testid="co-email" />
              <TextField label="Website" value={company.website} onChange={setC("website")} testid="co-website" />
              <TextField label="Address" value={company.address} onChange={setC("address")} testid="co-address" className="sm:col-span-2" />
              <TextField label="Target Purchase %" type="number" value={company.target_purchase_percentage} onChange={setC("target_purchase_percentage")} testid="co-target" />
              <TextField label="Default Closing Cost %" type="number" value={company.default_closing_cost_percentage} onChange={setC("default_closing_cost_percentage")} testid="co-closing" />
              <TextField label="Default Selling Cost %" type="number" value={company.default_selling_cost_percentage} onChange={setC("default_selling_cost_percentage")} testid="co-selling" />
              <TextField label="Default Repair Contingency %" type="number" value={company.default_repair_contingency} onChange={setC("default_repair_contingency")} testid="co-repair" />
              <div className="sm:col-span-2"><Button onClick={saveCompany} data-testid="co-save">Save Company Settings</Button></div>
            </div>
          </TabsContent>

          <TabsContent value="plan" className="mt-5">
            <PlanTab />
          </TabsContent>

          <TabsContent value="security" className="mt-5">
            <SecurityTab />
          </TabsContent>

          {isAdmin && <TabsContent value="usage" className="mt-5">
            <UsageTab />
          </TabsContent>}

          {isAdmin && <TabsContent value="audit" className="mt-5">
            <AuditLogTab />
          </TabsContent>}


          <TabsContent value="users" className="mt-5">
            <div className="flex flex-wrap justify-end gap-2 mb-4">
              <Dialog open={invOpen} onOpenChange={(o) => { setInvOpen(o); if (!o) { setInv({ role: "Read Only" }); setInviteLink(""); setInvError(""); } }}>
                <DialogTrigger asChild><Button variant="outline" data-testid="invite-user-btn"><Mail className="w-4 h-4 mr-1" /> Invite Teammate</Button></DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle className="font-display">Invite Teammate</DialogTitle></DialogHeader>
                  {!inviteLink ? (
                    <>
                      {invError && <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md px-3 py-2" data-testid="invite-form-error">{invError}</div>}
                      <div className="grid grid-cols-2 gap-4">
                        <TextField label="First Name" value={inv.first_name} onChange={setINV("first_name")} testid="inv-first" />
                        <TextField label="Last Name" value={inv.last_name} onChange={setINV("last_name")} testid="inv-last" />
                        <TextField label="Email" value={inv.email} onChange={setINV("email")} testid="inv-email" className="col-span-2" />
                        <SelectField label="Role" value={inv.role} onChange={setINV("role")} options={USER_ROLE} testid="inv-role" />
                      </div>
                      <DialogFooter><Button onClick={sendInvite} data-testid="inv-submit">Create Invite Link</Button></DialogFooter>
                    </>
                  ) : (
                    <div className="space-y-3" data-testid="invite-link-result">
                      <p className="text-sm text-muted-foreground">Share this secure link with your teammate — they'll set their own password. It expires in 7 days. (A mock email was also logged.)</p>
                      <div className="flex items-center gap-2">
                        <Input readOnly value={inviteLink} data-testid="invite-link-input" className="font-mono-nums text-xs" />
                        <Button variant="outline" size="icon" onClick={copyInvite} data-testid="copy-invite-btn"><Copy className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild><Button data-testid="add-user-btn"><Plus className="w-4 h-4 mr-1" /> Add User</Button></DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle className="font-display">Add Team Member</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-4">
                    <TextField label="First Name" value={nu.first_name} onChange={setNU("first_name")} testid="u-first" />
                    <TextField label="Last Name" value={nu.last_name} onChange={setNU("last_name")} testid="u-last" />
                    <TextField label="Email" value={nu.email} onChange={setNU("email")} testid="u-email" className="col-span-2" />
                    <TextField label="Password" value={nu.password} onChange={setNU("password")} testid="u-password" />
                    <SelectField label="Role" value={nu.role} onChange={setNU("role")} options={USER_ROLE} testid="u-role" />
                  </div>
                  <DialogFooter><Button onClick={addUser} data-testid="u-submit">Create User</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-card border border-border rounded-md overflow-x-auto">
              <Table><TableHeader><TableRow className="bg-secondary"><TableHead>Name</TableHead><TableHead className="hidden sm:table-cell">Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead>{isAdmin && <TableHead className="text-right">Seat</TableHead>}</TableRow></TableHeader>
                <TableBody>{users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.first_name} {u.last_name}<span className="block sm:hidden text-xs font-normal text-muted-foreground truncate max-w-[160px]">{u.email}</span></TableCell>
                    <TableCell className="hidden sm:table-cell text-muted-foreground">{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className="bg-primary/5">{u.role}</Badge></TableCell>
                    <TableCell>{u.active ? <Badge variant="outline" className="bg-green-100 text-green-800">Active</Badge> : u.status === "invited" ? <Badge variant="outline" className="bg-amber-100 text-amber-800">Invited</Badge> : <Badge variant="outline" className="bg-slate-100">Revoked</Badge>}</TableCell>
                    {isAdmin && <TableCell className="text-right">
                      {u.id === me?.id ? <span className="text-xs text-muted-foreground">You</span>
                        : u.active ? <span className="inline-flex gap-2 justify-end">
                            <Button size="sm" variant="outline" onClick={() => genResetLink(u)} data-testid={`reset-password-${u.id}`}>Reset password</Button>
                            <Button size="sm" variant="outline" onClick={() => { setReset2faUser(u); setReauth({ password: "", code: "" }); }} data-testid={`reset-2fa-${u.id}`}>Reset 2FA</Button>
                            <Button size="sm" variant="outline" className="text-amber-700 border-amber-500/40 hover:bg-amber-500/5" onClick={() => { setForceUser(u); setForceReason(""); }} data-testid={`force-reset-${u.id}`}>Force reset</Button>
                            <Button size="sm" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/5" onClick={() => revokeSeat(u)} data-testid={`revoke-seat-${u.id}`}>Revoke seat</Button>
                          </span>
                        : <span className="inline-flex gap-2 justify-end">
                            <Button size="sm" variant="outline" onClick={() => reactivateSeat(u)} data-testid={`reactivate-seat-${u.id}`}>Reactivate</Button>
                            <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/5" onClick={() => deleteMember(u)} data-testid={`delete-user-${u.id}`}>Remove</Button>
                          </span>}
                    </TableCell>}
                  </TableRow>))}</TableBody></Table>
            </div>
            <Dialog open={!!resetInfo} onOpenChange={(o) => !o && setResetInfo(null)}>
              <DialogContent data-testid="reset-link-dialog">
                <DialogHeader><DialogTitle>Password reset link</DialogTitle>
                  <DialogDescription>A one-time link to reset this user's password.</DialogDescription>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">Share this one-time link with <span className="text-foreground font-medium">{resetInfo?.name}</span>. It expires in {resetInfo?.expires_in_hours} hours and works once.</p>
                <div className="flex items-center gap-2 mt-2">
                  <Input readOnly value={resetInfo?.reset_link || ""} data-testid="reset-link-value" className="text-xs" />
                  <Button variant="outline" size="icon" data-testid="copy-reset-link-btn" onClick={async () => { try { await navigator.clipboard?.writeText(resetInfo?.reset_link || ""); toast.success("Reset link copied"); } catch { toast.error("Couldn't copy — select the link and copy manually."); } }}><Copy className="w-4 h-4" /></Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={!!forceUser} onOpenChange={(o) => !o && setForceUser(null)}>
              <DialogContent data-testid="force-reset-dialog">
                <DialogHeader><DialogTitle>Force password reset</DialogTitle>
                  <DialogDescription>Require this user to set a new password before they can use the app again.</DialogDescription>
                </DialogHeader>
                <p className="text-sm text-muted-foreground"><span className="text-foreground font-medium">{forceUser?.first_name} {forceUser?.last_name}</span> ({forceUser?.email}) will be signed out of the app and prompted to change their password on their next request. Use this after a suspected compromise or other security event.</p>
                <div className="space-y-1.5 mt-2">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Reason (recorded in the audit log)</Label>
                  <Input value={forceReason} onChange={(e) => setForceReason(e.target.value)} placeholder="e.g. Suspected credential compromise" data-testid="force-reset-reason" />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setForceUser(null)} data-testid="force-reset-cancel">Cancel</Button>
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white" disabled={forceBusy} onClick={doForceReset} data-testid="force-reset-confirm">{forceBusy ? "Applying…" : "Force reset"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={!!reset2faUser} onOpenChange={(o) => !o && setReset2faUser(null)}>
              <DialogContent data-testid="reset-2fa-dialog">
                <DialogHeader><DialogTitle>Reset two-factor authentication</DialogTitle>
                  <DialogDescription>Clears this user's authenticator so they enroll again on next sign-in.</DialogDescription>
                </DialogHeader>
                <p className="text-sm text-muted-foreground"><span className="text-foreground font-medium">{reset2faUser?.first_name} {reset2faUser?.last_name}</span> ({reset2faUser?.email}) will be asked to set up their authenticator app again. Confirm it's you to continue — this is recorded in the audit log and the user is notified by email.</p>
                <div className="space-y-3 mt-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Your password</Label>
                    <Input type="password" value={reauth.password} onChange={(e) => setReauth((p) => ({ ...p, password: e.target.value }))} data-testid="reset-2fa-password" />
                  </div>
                  {me?.two_factor_enabled && (
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Your authenticator code</Label>
                      <Input value={reauth.code} onChange={(e) => setReauth((p) => ({ ...p, code: e.target.value }))} inputMode="numeric" placeholder="123456" data-testid="reset-2fa-code" />
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setReset2faUser(null)} data-testid="reset-2fa-cancel">Cancel</Button>
                  <Button disabled={reset2faBusy || !reauth.password} onClick={doReset2fa} data-testid="reset-2fa-confirm">{reset2faBusy ? "Resetting…" : "Reset 2FA"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="county" className="mt-5">
            <CountyOfficesTab />
          </TabsContent>

          <TabsContent value="formulas" className="mt-5">
            <div className="bg-card border border-border rounded-md divide-y divide-border max-w-3xl">
              {FORMULAS.map(([name, f]) => (
                <div key={name} className="p-4"><div className="font-display font-bold">{name}</div><div className="text-sm text-muted-foreground font-mono-nums mt-1">{f}</div></div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


function CountyOfficesTab() {
  const [offices, setOffices] = useState([]);
  const [open, setOpen] = useState(false);
  const [covOpen, setCovOpen] = useState(false);
  const [covStates, setCovStates] = useState([]);
  const [cov, setCov] = useState({ state_abbreviation: "", counties: "" });
  const [o, setO] = useState({ office_type: "Assessor", active: true, last_verified_date: new Date().toISOString().slice(0, 10) });
  const set = (k) => (v) => setO((p) => ({ ...p, [k]: v }));
  const load = () => api.get("/county-offices").then((r) => setOffices(r.data));
  useEffect(() => { load(); api.get("/county-offices/coverage-states").then((r) => setCovStates(r.data)); }, []);

  const add = async () => {
    try { await api.post("/county-offices", o); toast.success("County office added"); setOpen(false); setO({ office_type: "Assessor", active: true, last_verified_date: new Date().toISOString().slice(0, 10) }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
  };
  const bulkImport = async () => {
    try { const { data } = await api.post("/county-offices/bulk-import"); toast.success(`Imported ${data.added} counties (${data.skipped} already present)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };
  const generateCoverage = async () => {
    const isOther = cov.state_abbreviation === "Other";
    const sa = isOther ? (cov.abbr_custom || "").trim().toUpperCase() : cov.state_abbreviation;
    if (!sa) return toast.error("Pick or enter a state");
    const counties = cov.counties.split("\n").map((c) => c.trim()).filter(Boolean);
    if (isOther && counties.length === 0) return toast.error("Paste county names for a custom state");
    const sel = covStates.find((s) => s.abbreviation === sa);
    try {
      const { data } = await api.post("/county-offices/generate-coverage", { state_abbreviation: sa, state_name: sel?.state_name || cov.name_custom, counties });
      toast.success(`Generated ${data.added} counties for ${data.state} (${data.skipped} already present)`);
      setCovOpen(false); setCov({ state_abbreviation: "", counties: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate coverage"); }
  };
  const reverify = async (c) => {
    try { await api.post(`/county-offices/${c.id}/verify`); toast.success(`${c.county_name} re-verified today`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-end gap-2 mb-4">
        <Dialog open={covOpen} onOpenChange={setCovOpen}>
          <DialogTrigger asChild><Button variant="outline" data-testid="generate-coverage-btn"><MapPinned className="w-4 h-4 mr-1" /> Generate State Coverage</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle className="font-display">Generate Full County Coverage</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Creates a records portal (NETROnline hub + GIS/foreclosure search) for every county in a state. Pick a supported state, or choose "Other" and paste county names (one per line) for any state you operate in. Generated links should be independently re-verified.</p>
              <SelectField label="State" value={cov.state_abbreviation} onChange={(v) => setCov((p) => ({ ...p, state_abbreviation: v }))} options={[...covStates.map((s) => s.abbreviation), "Other"]} testid="cov-state" />
              {cov.state_abbreviation === "Other" && (
                <div className="grid grid-cols-2 gap-3">
                  <TextField label="State Abbreviation" value={cov.abbr_custom} onChange={(v) => setCov((p) => ({ ...p, abbr_custom: v }))} testid="cov-abbr-custom" />
                  <TextField label="State Name" value={cov.name_custom} onChange={(v) => setCov((p) => ({ ...p, name_custom: v }))} testid="cov-name-custom" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">County names (one per line — required for "Other", optional otherwise)</Label>
                <Textarea rows={6} value={cov.counties} onChange={(e) => setCov((p) => ({ ...p, counties: e.target.value }))} placeholder={"Los Angeles\nOrange\nSan Diego"} data-testid="cov-counties" />
              </div>
            </div>
            <DialogFooter><Button onClick={generateCoverage} data-testid="cov-submit">Generate Coverage</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Button variant="outline" onClick={bulkImport} data-testid="bulk-import-btn"><Download className="w-4 h-4 mr-1" /> Import Starter Set</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-county-btn"><Plus className="w-4 h-4 mr-1" /> Add County Office</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle className="font-display">Add County Office</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="State Name" value={o.state_name} onChange={set("state_name")} testid="co-state-name" />
              <TextField label="State Abbreviation" value={o.state_abbreviation} onChange={set("state_abbreviation")} testid="co-state-abbr" />
              <TextField label="County Name" value={o.county_name} onChange={set("county_name")} testid="co-county-name" />
              <TextField label="Office Name" value={o.office_name} onChange={set("office_name")} testid="co-office-name" />
              <SelectField label="Office Type" value={o.office_type} onChange={set("office_type")} options={OFFICE_TYPE} testid="co-office-type" />
              <TextField label="Phone Number" value={o.phone_number} onChange={set("phone_number")} testid="co-phone" />
              <TextField label="Property Search URL" value={o.property_search_url} onChange={set("property_search_url")} testid="co-property-url" className="sm:col-span-2" />
              <TextField label="GIS Map URL" value={o.gis_map_url} onChange={set("gis_map_url")} testid="co-gis-url" className="sm:col-span-2" />
              <TextField label="Tax Collector URL" value={o.tax_collector_url} onChange={set("tax_collector_url")} testid="co-tax-url" className="sm:col-span-2" />
              <TextField label="Recorder URL" value={o.recorder_url} onChange={set("recorder_url")} testid="co-recorder-url" className="sm:col-span-2" />
              <TextField label="Foreclosure Search URL" value={o.foreclosure_search_url} onChange={set("foreclosure_search_url")} testid="co-foreclosure-url" className="sm:col-span-2" />
              <TextField label="Last Verified Date" type="date" value={o.last_verified_date} onChange={set("last_verified_date")} testid="co-verified-date" />
            </div>
            <DialogFooter><Button onClick={add} data-testid="co-submit">Add Office</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-card border border-border rounded-md overflow-hidden">
        <Table><TableHeader><TableRow className="bg-secondary"><TableHead>County</TableHead><TableHead>State</TableHead><TableHead>Office</TableHead><TableHead>Type</TableHead><TableHead>Verified</TableHead><TableHead>By</TableHead><TableHead>Active</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {offices.map((c) => (
              <TableRow key={c.id} data-testid={`county-row-${c.id}`}>
                <TableCell className="font-medium">{c.county_name}</TableCell>
                <TableCell>{c.state_abbreviation || c.state_name}</TableCell>
                <TableCell className="text-muted-foreground">{c.office_name}</TableCell>
                <TableCell>{c.office_type}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{fmtDate(c.last_verified_date)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.verified_by || "—"}</TableCell>
                <TableCell>{c.active ? <Badge variant="outline" className="bg-green-100 text-green-800">Active</Badge> : <Badge variant="outline" className="bg-slate-100">Inactive</Badge>}</TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => reverify(c)} data-testid={`county-verify-${c.id}`}><BadgeCheck className="w-3.5 h-3.5 mr-1" /> Re-verify</Button></TableCell>
              </TableRow>
            ))}
            {offices.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No county offices yet. Click "Import Starter Set" to load major counties, or add one to enable Official County Records links on matching properties.</TableCell></TableRow>}
          </TableBody></Table>
      </div>
    </div>
  );
}

const PLAN_META = {
  explorer: { name: "Explorer", price: "$19.99/mo" },
  professional: { name: "Professional", price: "$49.99/mo" },
  enterprise: { name: "Enterprise", price: "$149/mo" },
};

function UsageBar({ label, used, limit }) {
  const unlimited = limit == null;
  const pct = unlimited ? 12 : Math.min(100, Math.round((used / limit) * 100));
  const near = !unlimited && pct >= 80;
  return (
    <div data-testid={`usage-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono-nums">{used}{unlimited ? " / ∞" : ` / ${limit}`}</span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full rounded-full ${unlimited ? "bg-emerald-500" : near ? "bg-destructive" : "bg-primary"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function PlanTab() {
  const [p, setP] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  useEffect(() => { api.get("/plan").then((r) => setP(r.data)); }, []);
  if (!p) return <div className="text-muted-foreground">Loading plan…</div>;
  const meta = PLAN_META[p.plan] || { name: p.plan, price: "" };
  const l = p.limits, u = p.usage;
  const trialing = p.plan_status === "trialing" && p.trial_days_left != null;

  const openPortal = async () => {
    setPortalBusy(true);
    try { const { data } = await api.post("/payments/portal"); window.location.href = data.url; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not open billing portal"); setPortalBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="bg-primary text-primary-foreground rounded-md p-6 flex flex-wrap items-center justify-between gap-4" data-testid="current-plan-card">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent">Current Plan</div>
          <div className="font-display text-2xl sm:text-3xl font-bold mt-1">{meta.name}</div>
          <div className="text-white/60 text-sm mt-1">{meta.price} · {l.commercial ? "commercial modules included" : "residential only"}</div>
          {trialing && <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold bg-accent/20 text-accent border border-accent/30 rounded-full px-2.5 py-1" data-testid="plan-trial-badge">{p.trial_days_left === 0 ? "Trial ends today" : `${p.trial_days_left} day${p.trial_days_left === 1 ? "" : "s"} left in trial`}</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={openPortal} disabled={portalBusy} data-testid="manage-billing-btn" className="bg-white/10 text-white px-4 py-2.5 rounded-md text-sm font-semibold hover:bg-white/20 transition-colors disabled:opacity-60">{portalBusy ? "Opening…" : "Manage Billing"}</button>
          <Link to="/pricing" data-testid="manage-plan-btn" className="bg-accent text-accent-foreground px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-accent/90 transition-colors">Change Plan</Link>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md p-6 space-y-5">
        <div className="font-display font-bold">Usage this billing period</div>
        <UsageBar label="Saved Leads" used={u.leads} limit={l.leads} />
        <UsageBar label="Deal Analyses (month)" used={u.analyses_this_month} limit={l.analyses_per_month} />
        <UsageBar label="Team Members" used={u.users} limit={l.users} />
        {!l.commercial && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">Commercial &amp; multifamily underwriting is available on Professional and above.</div>}
      </div>
    </div>
  );
}


function SecurityTab() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(!!user?.two_factor_enabled);
  const [setup, setSetup] = useState(null); // {qr_code, secret}
  const [code, setCode] = useState("");
  const [backups, setBackups] = useState(null);
  const [disableCode, setDisableCode] = useState("");
  const [regenCode, setRegenCode] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start 2FA setup"); }
    finally { setBusy(false); }
  };
  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setBackups(data.backup_codes); setEnabled(true); setSetup(null); setCode("");
      toast.success("Two-factor authentication enabled");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      setEnabled(false); setBackups(null); setDisableCode("");
      toast.success("Two-factor authentication disabled");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };
  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/backup-codes/regenerate", { code: regenCode });
      setBackups(data.backup_codes); setRegenCode("");
      toast.success("New backup codes generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="security-tab">
      <div className="bg-card border border-border rounded-md p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-display font-bold"><ShieldCheck className="w-5 h-5 text-accent" /> Two-Factor Authentication</div>
            <p className="text-sm text-muted-foreground mt-1">Add a one-time code from an authenticator app (Google Authenticator, Authy) to your sign-in.</p>
          </div>
          <Badge variant="outline" className={enabled ? "bg-green-100 text-green-800" : "bg-slate-100"} data-testid="2fa-status">{enabled ? "Enabled" : "Disabled"}</Badge>
        </div>

        {!enabled && !setup && (
          <Button className="mt-5" onClick={startSetup} disabled={busy} data-testid="2fa-setup-btn">{busy ? "Preparing…" : "Enable 2FA"}</Button>
        )}

        {!enabled && setup && (
          <div className="mt-5 space-y-4" data-testid="2fa-setup-panel">
            <p className="text-sm">1. Scan this QR code with your authenticator app:</p>
            <img src={setup.qr_code} alt="2FA QR code" className="w-44 h-44 border border-border rounded-md" data-testid="2fa-qr" />
            <p className="text-xs text-muted-foreground">Or enter this key manually: <span className="font-mono-nums">{setup.secret}</span></p>
            <div className="space-y-1.5 max-w-xs">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">2. Enter the 6-digit code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="123456" data-testid="2fa-verify-code" />
            </div>
            <Button onClick={enable} disabled={busy} data-testid="2fa-enable-btn">{busy ? "Verifying…" : "Verify & Enable"}</Button>
          </div>
        )}

        {backups && (
          <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="2fa-backup-codes">
            <div className="font-medium text-amber-900 text-sm">Save your backup codes</div>
            <p className="text-xs text-amber-800 mt-1">Each code works once if you lose your device. Store them somewhere safe — they won't be shown again.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 font-mono-nums text-sm">
              {backups.map((c) => <div key={c} className="bg-white border border-amber-200 rounded px-2 py-1 text-center">{c}</div>)}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => { navigator.clipboard?.writeText(backups.join("\n")); toast.success("Backup codes copied"); }} data-testid="2fa-backup-copy">Copy codes</Button>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { setBackups(null); toast.success("Great — keep those codes somewhere safe."); }} data-testid="2fa-backup-ack">I've saved my backup codes</Button>
            </div>
          </div>
        )}

        {enabled && (
          <div className="mt-5 space-y-4">
            <div className="space-y-3" data-testid="2fa-disable-panel">
              <div className="space-y-1.5 max-w-xs">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Enter a current code to disable</Label>
                <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} inputMode="numeric" placeholder="123456" data-testid="2fa-disable-code" />
              </div>
              <Button variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/5" onClick={disable} disabled={busy} data-testid="2fa-disable-btn">{busy ? "Disabling…" : "Disable 2FA"}</Button>
            </div>
            <div className="border-t border-border pt-4 space-y-3" data-testid="2fa-regen-panel">
              <div>
                <div className="text-sm font-medium">Backup codes</div>
                <p className="text-xs text-muted-foreground">Lost your codes or used them up? Enter a current authenticator code to generate a fresh set (old codes stop working).</p>
              </div>
              <div className="space-y-1.5 max-w-xs">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Authenticator code</Label>
                <Input value={regenCode} onChange={(e) => setRegenCode(e.target.value)} inputMode="numeric" placeholder="123456" data-testid="2fa-regen-code" />
              </div>
              <Button variant="outline" onClick={regenerate} disabled={busy} data-testid="2fa-regen-btn">{busy ? "Generating…" : "Regenerate backup codes"}</Button>
            </div>
          </div>
        )}
      </div>
      <TrustedDevicesCard />
      <ChangePasswordCard />
      {["System Owner", "Administrator"].includes(user?.role) && <SecurityPolicyCard />}
      {["System Owner", "Administrator"].includes(user?.role) && <SecurityHealthCard />}
      {["System Owner", "Administrator"].includes(user?.role) && <BackupCard />}
      {["System Owner", "Administrator"].includes(user?.role) && <RestoreCard />}
    </div>
  );
}

function TrustedDevicesCard() {
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/auth/trusted-devices").then((r) => setDevices(r.data)).catch(() => {}); }, []);
  const revokeAll = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/trusted-devices");
      setDevices([]);
      toast.success("All trusted devices removed — 2FA codes will be required again");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove trusted devices"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-card border border-border rounded-md p-6" data-testid="trusted-devices-card">
      <div className="flex items-center gap-2 font-display font-bold"><BadgeCheck className="w-5 h-5 text-accent" /> Trusted Devices</div>
      <p className="text-sm text-muted-foreground mt-1">Devices you've ticked "Trust this device" on skip the 2FA code for 30 days. Remove them anytime if a device is lost or shared.</p>
      {devices.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-4" data-testid="trusted-devices-empty">No trusted devices yet. Tick "Trust this device" the next time you enter a 2FA code.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {devices.map((d) => (
            <div key={d.id} className="flex items-center justify-between border border-border rounded-md px-3 py-2 text-sm" data-testid={`trusted-device-${d.id}`}>
              <div>
                <div className="font-medium">{d.device || "Unknown device"}</div>
                <div className="text-xs text-muted-foreground">IP {d.ip} · trusted {fmtDate(d.created_at)} · expires {fmtDate(d.expires_at)}</div>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/5 mt-2" onClick={revokeAll} disabled={busy} data-testid="trusted-devices-revoke-all">{busy ? "Removing…" : "Remove all trusted devices"}</Button>
        </div>
      )}
    </div>
  );
}

function SecurityPolicyCard() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/security/settings").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/security/settings", {
        max_failed_attempts: Number(cfg.max_failed_attempts),
        lockout_minutes: Number(cfg.lockout_minutes),
        escalate_lockouts: !!cfg.escalate_lockouts,
        force_reset_enabled: !!cfg.force_reset_enabled,
      });
      setCfg(data);
      toast.success("Security settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save settings"); }
    finally { setBusy(false); }
  };
  if (!cfg) return null;
  return (
    <div className="bg-card border border-border rounded-md p-6" data-testid="security-policy-card">
      <div className="flex items-center gap-2 font-display font-bold"><Lock className="w-5 h-5 text-accent" /> Login Security Policy</div>
      <p className="text-sm text-muted-foreground mt-1">Control how the app responds to repeated failed sign-ins and whether admins can force password resets.</p>
      <div className="grid sm:grid-cols-2 gap-5 mt-5">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Max failed attempts</Label>
          <Input type="number" min={3} max={20} value={cfg.max_failed_attempts} onChange={(e) => set("max_failed_attempts", e.target.value)} data-testid="policy-max-attempts" />
          <p className="text-xs text-muted-foreground">Lock the account after this many failures (3–20).</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Lockout duration (minutes)</Label>
          <Input type="number" min={1} max={1440} value={cfg.lockout_minutes} onChange={(e) => set("lockout_minutes", e.target.value)} data-testid="policy-lockout-minutes" />
          <p className="text-xs text-muted-foreground">How long the lockout lasts (1–1440).</p>
        </div>
      </div>
      <div className="mt-5 space-y-3">
        <label className="flex items-center justify-between gap-4 rounded-md border border-border p-3 cursor-pointer" data-testid="policy-escalate-row">
          <span className="text-sm"><span className="font-medium">Escalate repeated lockouts</span><br /><span className="text-muted-foreground text-xs">Double the lockout duration each time the same identifier is locked again.</span></span>
          <input type="checkbox" className="w-4 h-4 accent-accent" checked={!!cfg.escalate_lockouts} onChange={(e) => set("escalate_lockouts", e.target.checked)} data-testid="policy-escalate" />
        </label>
        <label className="flex items-center justify-between gap-4 rounded-md border border-border p-3 cursor-pointer" data-testid="policy-force-reset-row">
          <span className="text-sm"><span className="font-medium">Allow forced password resets</span><br /><span className="text-muted-foreground text-xs">Let admins require a user to reset their password (e.g. after suspected compromise).</span></span>
          <input type="checkbox" className="w-4 h-4 accent-accent" checked={!!cfg.force_reset_enabled} onChange={(e) => set("force_reset_enabled", e.target.checked)} data-testid="policy-force-reset" />
        </label>
      </div>
      <div className="flex items-center justify-between mt-5">
        <span className="text-xs text-muted-foreground">{cfg.updated_by ? `Last updated by ${cfg.updated_by}` : "Using default policy"}</span>
        <Button onClick={save} disabled={busy} data-testid="policy-save">{busy ? "Saving…" : "Save policy"}</Button>
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (next !== confirm) { toast.error("New password and confirmation do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed successfully.");
      setCur(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change password.");
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-card border border-border rounded-md p-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 font-display font-bold"><ShieldCheck className="w-5 h-5 text-accent" /> Change Password</div>
      <p className="text-sm text-muted-foreground mt-1">Use a strong password — at least 12 characters with an uppercase letter, a lowercase letter, and a number.</p>
      <div className="mt-5 space-y-4 max-w-sm">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Current password</Label>
          <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cp-current" autoComplete="current-password" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">New password</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new" autoComplete="new-password" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Confirm new password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm" autoComplete="new-password" />
        </div>
        <Button onClick={submit} disabled={busy || !cur || !next} data-testid="cp-submit">{busy ? "Saving…" : "Change Password"}</Button>
      </div>
    </div>
  );
}

const AUDIT_ACTION_COLORS = {
  login: "bg-slate-100 text-slate-700", create: "bg-blue-100 text-blue-800",
  update: "bg-amber-100 text-amber-800", deactivate: "bg-red-100 text-red-800",
  reactivate: "bg-green-100 text-green-800", plan_change: "bg-violet-100 text-violet-800",
  invite: "bg-indigo-100 text-indigo-800", accept_invite: "bg-emerald-100 text-emerald-800",
};

function AuditLogTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const load = useCallback(() => {
    const params = { limit: 300 };
    if (category !== "all") params.category = category;
    api.get("/audit-log", { params }).then((r) => setItems(r.data));
  }, [category]);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((i) => {
    const hay = [i.user_name, i.user_email, i.action, i.entity_type, JSON.stringify(i.meta || {})].join(" ").toLowerCase();
    return !q.trim() || hay.includes(q.trim().toLowerCase());
  });

  return (
    <div data-testid="audit-log-tab">
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <ScrollText className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search audit entries…" className="pl-9" data-testid="audit-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-56" data-testid="audit-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All events</SelectItem>
            <SelectItem value="password_reset">Password Reset</SelectItem>
            <SelectItem value="forced_reset">Forced Password Reset</SelectItem>
            <SelectItem value="failed_login">Failed Login</SelectItem>
            <SelectItem value="lockout">Account Lockout</SelectItem>
            <SelectItem value="security">Security Events</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-secondary"><TableHead>When</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead className="hidden sm:table-cell">Entity</TableHead><TableHead className="hidden md:table-cell">Details</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((i) => (
              <TableRow key={i.id} data-testid={`audit-row-${i.id}`}>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{i.created_at ? new Date(i.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</TableCell>
                <TableCell className="text-sm">{i.user_name || i.user_email || "System"}</TableCell>
                <TableCell><Badge variant="outline" className={AUDIT_ACTION_COLORS[i.action] || "bg-slate-100 text-slate-700"}>{i.action}</Badge></TableCell>
                <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{i.entity_type}</TableCell>
                <TableCell className="hidden md:table-cell text-xs text-muted-foreground font-mono-nums truncate max-w-[280px]">{i.meta && Object.keys(i.meta).length ? JSON.stringify(i.meta) : "—"}</TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No audit entries.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


const STATUS_STYLES = {
  "Healthy": { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  "Live": { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  "Action Required": { icon: AlertTriangle, cls: "text-amber-600 bg-amber-50 border-amber-200" },
  "Error": { icon: AlertTriangle, cls: "text-red-600 bg-red-50 border-red-200" },
  "Not Configured": { icon: HelpCircle, cls: "text-muted-foreground bg-muted border-border" },
  "Missing": { icon: HelpCircle, cls: "text-muted-foreground bg-muted border-border" },
  "Demo": { icon: Activity, cls: "text-blue-600 bg-blue-50 border-blue-200" },
  "Test Mode": { icon: Activity, cls: "text-blue-600 bg-blue-50 border-blue-200" },
  "Unknown": { icon: HelpCircle, cls: "text-muted-foreground bg-muted border-border" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES["Unknown"];
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${s.cls}`} data-testid="health-status-pill">
      <Icon className="w-3.5 h-3.5" /> {status}
    </span>
  );
}

function SecurityHealthCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/security/health"); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load security health"); }
  };
  useEffect(() => { load(); }, []);
  const refresh = async () => {
    setBusy(true);
    try { const { data } = await api.post("/security/health/refresh"); setData(data); toast.success("Security status refreshed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
    finally { setBusy(false); }
  };
  if (!data) return null;
  const p = data.open_findings || {};
  const g = data.deploy_gate || {};
  const gateStyle = g.status === "Ready to Deploy" ? "bg-emerald-50 border-emerald-300 text-emerald-800"
    : g.status === "Not Ready to Deploy" ? "bg-red-50 border-red-300 text-red-800"
    : "bg-amber-50 border-amber-300 text-amber-800";
  const GateIcon = g.status === "Ready to Deploy" ? CheckCircle2 : g.status === "Not Ready to Deploy" ? AlertTriangle : HelpCircle;
  return (
    <div className="bg-card border border-border rounded-md p-6" data-testid="security-health-card">
      <div className={`rounded-md border-2 p-4 flex items-start justify-between gap-4 flex-wrap ${gateStyle}`} data-testid="deploy-gate-badge" data-status={g.status}>
        <div className="flex items-center gap-3">
          <GateIcon className="w-7 h-7" />
          <div>
            <div className="font-display text-lg font-bold" data-testid="deploy-gate-status">{g.status || "Status Unknown"}</div>
            <div className="text-xs opacity-80">P1 blockers: {g.open_p1_count ?? "?"} · P2 blockers: {g.open_p2_count ?? "?"}{g.last_audit_at ? ` · Audited ${fmtDate(g.last_audit_at)}` : " · No recent audit"}</div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={busy} data-testid="health-refresh-btn"><RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh Status</Button>
      </div>
      {(g.blocking_findings || []).length > 0 && (
        <div className="mt-3 text-xs text-muted-foreground" data-testid="deploy-gate-blockers">
          <span className="font-semibold text-foreground">Blocking deployment:</span> {g.blocking_findings.map((b) => b.label).join(" · ")}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap mt-5">
        <div className="flex items-center gap-2 font-display font-bold"><ShieldAlert className="w-5 h-5 text-accent" /> Security Health</div>
        <span className="text-xs text-muted-foreground" data-testid="health-open-findings">Open findings: <b className="text-foreground">P1 {p.p1 ?? 0}</b> · <b className="text-foreground">P2 {p.p2 ?? 0}</b></span>
      </div>
      <p className="text-xs text-muted-foreground mt-1">Status only — no secrets, keys, or credentials are ever shown. Deployment readiness is computed on the server.</p>
      <div className="mt-5 divide-y divide-border border border-border rounded-md">
        {data.checks.map((c) => (
          <div key={c.key} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`health-row-${c.key}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{c.label}</div>
              {c.detail && <div className="text-xs text-muted-foreground truncate">{c.detail}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <StatusPill status={c.status} />
              {["Action Required", "Missing", "Error", "Not Configured"].includes(c.status) && c.link &&
                <Link to={c.link} className="text-xs text-accent underline whitespace-nowrap" data-testid={`health-fix-${c.key}`}>Fix</Link>}
            </div>
          </div>
        ))}
      </div>
      {data.integrations_checked_at && <p className="text-xs text-muted-foreground mt-3">Integrations last verified: {fmtDate(data.integrations_checked_at)}</p>}
    </div>
  );
}

const hb = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"]; let i = 0; let v = Number(n);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${i === 0 ? v.toFixed(0) : v.toFixed(1)} ${u[i]}`;
};

function BackupCard() {
  const { user } = useAuth();
  const isOwner = user?.role === "System Owner";
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runType, setRunType] = useState("daily");
  const [rowBusy, setRowBusy] = useState({});

  const load = async () => {
    try {
      const [s, l] = await Promise.all([api.get("/backups/status"), api.get("/backups")]);
      setStatus(s.data); setItems(l.data.backups || []);
      api.get("/email/domain-status").then(({ data }) => setEmailStatus(data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load backup status"); }
  };
  useEffect(() => { load(); }, []);

  const setRB = (id, v) => setRowBusy((p) => ({ ...p, [id]: v }));
  const sendTestEmail = async () => {
    setBusy(true);
    try { const { data } = await api.post("/email/test"); toast[data.results?.some((r) => r.sent) ? "success" : "error"](data.results?.some((r) => r.sent) ? `Test email sent (verified: ${data.domain_verified})` : `Not delivered — domain status: ${data.domain_status}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Test email failed"); }
    finally { setBusy(false); }
  };
  const sendDigest = async () => {
    setBusy(true);
    try { const { data } = await api.post("/backups/digest/send", { force: true }); toast[data.sent ? "success" : "error"](data.sent ? `Weekly digest sent (${data.overall})` : `Digest not sent: ${data.reason}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Digest send failed"); }
    finally { setBusy(false); }
  };

  const run = async () => {
    setBusy(true);
    try { await api.post("/backups/run", { type: runType }); toast.success(`${runType} backup created`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Backup failed"); }
    finally { setBusy(false); }
  };
  const cleanup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/backups/cleanup"); toast.success(`Retention cleanup done (${data.pruned} pruned)`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cleanup failed"); }
    finally { setBusy(false); }
  };
  const verify = async (id) => {
    setRB(id, true);
    try { const { data } = await api.post(`/backups/${id}/verify`); toast[data.ok ? "success" : "error"](data.ok ? "Backup verified (checksum + counts OK)" : "Verification failed"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
    finally { setRB(id, false); }
  };
  const restoreTest = async (id) => {
    setRB(id, true);
    try { const { data } = await api.post(`/backups/${id}/restore-test`); toast[data.matches_manifest ? "success" : "error"](data.matches_manifest ? `Restore OK → ${data.target_db} (${data.total_docs} docs)` : "Restore mismatch"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore test failed"); }
    finally { setRB(id, false); }
  };
  const restoreStaging = async (id) => {
    setRB(id, true);
    try { const { data } = await api.post(`/restore/staging`, { backup_id: id }); toast[data.matches_manifest && data.validation?.ok ? "success" : "error"](data.matches_manifest && data.validation?.ok ? `Staged & validated → ${data.staging_db} (${data.total_docs} docs)` : "Staging validation had warnings — check restore panel"); }
    catch (e) { toast.error(e.response?.data?.detail || "Staging restore failed"); }
    finally { setRB(id, false); }
  };
  const download = async (id, type) => {
    setRB(id, true);
    try {
      const res = await api.get(`/backups/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${type}-${id.slice(0, 8)}.gjbk`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Encrypted backup downloaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
    finally { setRB(id, false); }
  };

  if (!status) return null;
  const nextDates = status.next_scheduled || {};
  const nextVals = Object.values(nextDates).filter(Boolean).sort();
  const rt = status.last_restore_test;
  const summary = [
    ["Last successful backup", status.last_successful ? `${status.last_successful.type} · ${fmtDate(status.last_successful.created_at)}` : "None yet"],
    ["Backup size", status.last_successful ? hb(status.last_successful.size_bytes) : "—"],
    ["Storage provider", status.provider],
    ["Encryption", status.encryption ? status.algorithm : "Not configured"],
    ["Last restore test", rt ? `${rt.target_db} · ${rt.total_docs} docs · ${fmtDate(rt.tested_at)}` : "Not run yet"],
    ["Next scheduled backup", status.enabled ? (nextVals[0] ? fmtDate(nextVals[0]) : "—") : "Automatic backups off"],
  ];

  return (
    <div className="bg-card border border-border rounded-md p-6" data-testid="backup-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 font-display font-bold"><Database className="w-5 h-5 text-accent" /> Backup &amp; Restore</div>
        <Badge variant="outline" className={status.enabled ? "bg-green-100 text-green-800" : "bg-slate-100"} data-testid="backup-auto-status">{status.enabled ? "Automatic: On" : "Automatic: Off"}</Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-1">Encrypted, provider-agnostic MongoDB backups (daily / weekly / monthly). Restores are always verified into a non-production database.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-5" data-testid="backup-summary">
        {summary.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-3 border-b border-border/60 py-1.5">
            <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{k}</span>
            <span className="text-sm font-medium text-right" data-testid={`backup-summary-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-5">
        <Select value={runType} onValueChange={setRunType}>
          <SelectTrigger className="w-36" data-testid="backup-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly (full)</SelectItem>
            <SelectItem value="monthly">Monthly (archive)</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={run} disabled={busy || !status.encryption} data-testid="backup-run-btn"><PlayCircle className="w-4 h-4 mr-1.5" /> {busy ? "Working…" : "Run backup now"}</Button>
        <Button variant="outline" onClick={cleanup} disabled={busy} data-testid="backup-cleanup-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Apply retention</Button>
      </div>
      {!status.encryption && <p className="text-xs text-destructive mt-2" data-testid="backup-no-key">Set BACKUP_ENCRYPTION_KEY to enable backups.</p>}

      <div className="mt-5">
        <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-2">Recent backups (retention: {status.retention?.daily}d / {status.retention?.weekly}w / {status.retention?.monthly}m)</div>
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-md" data-testid="backup-empty">No backups yet.</div>
        ) : (
          <div className="border border-border rounded-md divide-y divide-border" data-testid="backup-list">
            {items.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 px-3 py-2.5 flex-wrap" data-testid={`backup-row-${b.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <span className="capitalize">{b.type}</span>
                    {b.status === "success" && <Badge variant="outline" className="bg-green-50 text-green-700 text-[10px]">success</Badge>}
                    {b.status === "failed" && <Badge variant="outline" className="bg-red-50 text-red-700 text-[10px]">failed</Badge>}
                    {b.verified_ok && <Badge variant="outline" className="bg-blue-50 text-blue-700 text-[10px]"><ShieldCheck className="w-3 h-3 mr-0.5" />verified</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground">{fmtDate(b.completed_at || b.created_at)} · {hb(b.size_bytes)} · {b.total_docs ?? 0} docs · {b.provider}</div>
                </div>
                {b.status === "success" && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button size="sm" variant="ghost" disabled={rowBusy[b.id]} onClick={() => verify(b.id)} data-testid={`backup-verify-${b.id}`}><BadgeCheck className="w-3.5 h-3.5 mr-1" />Verify</Button>
                    <Button size="sm" variant="ghost" disabled={rowBusy[b.id]} onClick={() => restoreTest(b.id)} data-testid={`backup-restore-${b.id}`}><Database className="w-3.5 h-3.5 mr-1" />Restore test</Button>
                    <Button size="sm" variant="ghost" disabled={rowBusy[b.id]} onClick={() => restoreStaging(b.id)} data-testid={`backup-staging-${b.id}`}><Database className="w-3.5 h-3.5 mr-1" />To staging</Button>
                    <Button size="sm" variant="ghost" disabled={rowBusy[b.id]} onClick={() => download(b.id, b.type)} data-testid={`backup-download-${b.id}`}><DownloadCloud className="w-3.5 h-3.5 mr-1" />Download</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-5 border-t border-border pt-4" data-testid="backup-email-section">
        <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-2">Email delivery &amp; weekly digest</div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm" data-testid="email-domain-status">
            Sender: <span className="font-medium">{emailStatus?.sender || "—"}</span>{" "}
            {emailStatus && (
              <Badge variant="outline" className={(emailStatus.verified || emailStatus.status === "restricted_key") ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}>
                {emailStatus.verified ? "domain verified" : emailStatus.status === "restricted_key" ? "send-ready (restricted key)" : `not verified (${emailStatus.status})`}
              </Badge>
            )}
          </div>
          {isOwner && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={sendTestEmail} disabled={busy} data-testid="send-test-email-btn"><Mail className="w-3.5 h-3.5 mr-1" />Send test email</Button>
              <Button size="sm" variant="outline" onClick={sendDigest} disabled={busy} data-testid="send-digest-btn"><ScrollText className="w-3.5 h-3.5 mr-1" />Send weekly digest</Button>
            </div>
          )}
        </div>
        {emailStatus && !emailStatus.verified && emailStatus.status !== "restricted_key" && <p className="text-xs text-amber-700 mt-2">Verify galahadji.com in Resend and set RESEND_FROM_EMAIL to enable production alert &amp; reset emails.</p>}
      </div>
    </div>
  );
}


const RESTORE_JOB_STYLES = {
  countdown: "bg-amber-50 text-amber-700", running: "bg-blue-50 text-blue-700",
  verifying: "bg-blue-50 text-blue-700", success: "bg-green-50 text-green-700",
  failed: "bg-red-50 text-red-700", cancelled: "bg-slate-100 text-slate-600",
  rolled_back: "bg-purple-50 text-purple-700",
};

function RestoreCard() {
  const { user } = useAuth();
  const isOwner = user?.role === "System Owner";
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ backup_id: "", entered_backup_id: "", confirm_phrase: "", password: "", totp_code: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/restore/jobs"); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load restore jobs"); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const doPreview = async () => {
    if (!form.backup_id) return toast.error("Enter a backup ID to preview");
    setBusy(true);
    try { const { data } = await api.post("/restore/production/preview", { backup_id: form.backup_id }); setPreview(data); toast.success("Preview generated (backup staged & validated)"); }
    catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };
  const requestRestore = async () => {
    setBusy(true);
    try { await api.post("/restore/production/request", form); toast.success("Restore requested — countdown started"); setPreview(null); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Request failed"); }
    finally { setBusy(false); }
  };
  const cancel = async (id) => { try { await api.post(`/restore/production/${id}/cancel`); toast.success("Restore cancelled"); await load(); } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); } };
  const rollback = async (id) => { try { await api.post(`/restore/production/${id}/rollback`); toast.success("Rolled back to safety backup"); await load(); } catch (e) { toast.error(e.response?.data?.detail || "Rollback failed"); } };

  if (!data) return null;
  const enabled = data.production_restore_enabled;
  const maint = data.maintenance || {};

  return (
    <div className="bg-card border border-border rounded-md p-6" data-testid="restore-card">
      <div className="flex items-center gap-2 font-display font-bold"><ShieldAlert className="w-5 h-5 text-accent" /> Database Restore</div>
      <p className="text-xs text-muted-foreground mt-1">Staging restores validate a backup in isolation. Production restore is System-Owner-only, disabled by default, and heavily guarded.</p>

      {maint.active && (
        <div className="mt-4 rounded-md border-2 border-amber-300 bg-amber-50 text-amber-800 p-3 text-sm" data-testid="maintenance-banner">
          <b>Maintenance mode is ON.</b> {maint.message}
        </div>
      )}

      <div className="mt-5">
        <div className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-2">Restore jobs</div>
        {data.jobs.length === 0 ? (
          <div className="text-sm text-muted-foreground py-3 text-center border border-dashed border-border rounded-md" data-testid="restore-jobs-empty">No restore jobs yet.</div>
        ) : (
          <div className="border border-border rounded-md divide-y divide-border" data-testid="restore-jobs-list">
            {data.jobs.map((j) => (
              <div key={j.id} className="flex items-center justify-between gap-3 px-3 py-2.5 flex-wrap" data-testid={`restore-job-${j.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <Badge variant="outline" className={`text-[10px] ${RESTORE_JOB_STYLES[j.status] || ""}`}>{j.status}</Badge>
                    <span className="font-mono-nums text-xs">{j.backup_id?.slice(0, 8)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">by {j.initiated_by_email || "—"} · {fmtDate(j.requested_at)}{j.safety_backup_id ? " · safety ✓" : ""}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {j.status === "countdown" && isOwner && <Button size="sm" variant="outline" onClick={() => cancel(j.id)} data-testid={`restore-cancel-${j.id}`}>Cancel</Button>}
                  {j.status === "failed" && isOwner && j.safety_backup_id && <Button size="sm" variant="outline" className="text-amber-700" onClick={() => rollback(j.id)} data-testid={`restore-rollback-${j.id}`}>Roll back</Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <div className="text-sm font-medium mb-1">Production restore</div>
        {!isOwner ? (
          <p className="text-xs text-muted-foreground" data-testid="restore-prod-owner-only">Only the System Owner can restore to production. Administrators can restore to staging (buttons in the Backup card above).</p>
        ) : !enabled ? (
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground" data-testid="restore-prod-disabled">
            Production restore is <b>disabled by default</b>. Set <span className="font-mono-nums text-xs">PRODUCTION_RESTORE_ENABLED=true</span> in the environment to enable it. All safeguards (staging validation, reauth, confirmation phrase, countdown, safety backup, rollback) then apply.
          </div>
        ) : (
          <div className="space-y-3" data-testid="restore-prod-form">
            <Input placeholder="Backup ID to restore" value={form.backup_id} onChange={(e) => setForm((p) => ({ ...p, backup_id: e.target.value }))} data-testid="restore-backup-id" />
            <Button variant="outline" onClick={doPreview} disabled={busy} data-testid="restore-preview-btn">Preview & validate</Button>
            {preview && (
              <div className="rounded-md border border-border p-3 text-xs space-y-1" data-testid="restore-preview">
                <div>Backup: {fmtDate(preview.backup_created_at)} · {hb(preview.backup_size_bytes)} · {preview.provider}</div>
                <div>Checksum OK: {String(preview.checksum_ok)} · Est. downtime ~{preview.estimated_downtime_seconds}s</div>
                <div>Collections affected: {preview.collections_affected?.length}</div>
                {preview.warnings?.length > 0 && <div className="text-amber-700">Warnings: {preview.warnings.join("; ")}</div>}
                <div className="pt-2 space-y-2 border-t border-border mt-2">
                  <Input placeholder='Type: RESTORE GALAHADJI PRODUCTION DATABASE' value={form.confirm_phrase} onChange={(e) => setForm((p) => ({ ...p, confirm_phrase: e.target.value }))} data-testid="restore-phrase" />
                  <Input placeholder="Re-enter the backup ID" value={form.entered_backup_id} onChange={(e) => setForm((p) => ({ ...p, entered_backup_id: e.target.value }))} data-testid="restore-entered-id" />
                  <Input type="password" placeholder="Your password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} data-testid="restore-password" />
                  <Input placeholder="Current 2FA code" value={form.totp_code} onChange={(e) => setForm((p) => ({ ...p, totp_code: e.target.value }))} data-testid="restore-totp" />
                  <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={requestRestore} disabled={busy} data-testid="restore-request-btn">Start guarded production restore</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const LIMIT_FIELDS = [
  ["attom_per_user_day", "ATTOM — per user / day"],
  ["skiptrace_per_user_day", "Skip-trace — per user / day"],
  ["company_per_day", "Company-wide / day (0 = unlimited)"],
  ["per_minute", "Per-minute rate limit"],
  ["monthly_budget_usd", "Monthly budget cap $ (0 = unlimited)"],
];

function UsageTab() {
  const [data, setData] = useState(null);
  const [limits, setLimits] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/usage/summary"); setData(data); setLimits(data.limits); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load usage"); }
  };
  useEffect(() => { load(); }, []);
  const saveLimits = async () => {
    setSaving(true);
    try { await api.put("/usage/limits", limits); toast.success("Limits updated"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save limits"); }
    finally { setSaving(false); }
  };
  const toggleAccess = async (uid, disabled) => {
    try { await api.put(`/users/${uid}/integration-access`, { disabled }); toast.success(disabled ? "Access disabled" : "Access restored"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update access"); }
  };
  const override = async (uid, provider) => {
    const extra = parseInt(window.prompt(`Extra ${provider} lookups to grant (expires in 24h):`, "25") || "0", 10);
    if (!extra) return;
    try { await api.post("/usage/override", { user_id: uid, provider, extra, hours: 24 }); toast.success("Override granted"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not grant override"); }
  };
  if (!data || !limits) return null;
  return (
    <div className="space-y-6" data-testid="usage-tab">
      <div className="bg-card border border-border rounded-md p-6">
        <div className="flex items-center gap-2 font-display font-bold"><Gauge className="w-5 h-5 text-accent" /> Lookup Limits</div>
        <p className="text-xs text-muted-foreground mt-1">Guardrails for billable ATTOM and skip-trace lookups. Blocked attempts never call the provider.</p>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {LIMIT_FIELDS.map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</Label>
              <Input type="number" min="0" value={limits[k]} onChange={(e) => setLimits({ ...limits, [k]: e.target.value === "" ? 0 : Number(e.target.value) })} data-testid={`limit-${k}`} />
            </div>
          ))}
        </div>
        <Button className="mt-4" onClick={saveLimits} disabled={saving} data-testid="save-limits-btn">{saving ? "Saving…" : "Save Limits"}</Button>
      </div>

      <div className="bg-card border border-border rounded-md p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 font-display font-bold"><Activity className="w-5 h-5 text-accent" /> Today's Usage</div>
          <div className="text-xs text-muted-foreground" data-testid="usage-totals">
            Company today — ATTOM {data.company_today.attom} · Skip-trace {data.company_today.skiptrace} · Blocked {data.blocked_today} · Month spend ${data.month_spend_usd?.toFixed?.(2) ?? "0.00"}
          </div>
        </div>
        <div className="mt-4 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>ATTOM</TableHead><TableHead>Skip-trace</TableHead><TableHead>Access</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.per_user.length === 0 && <TableRow><TableCell colSpan={6} className="text-sm text-muted-foreground">No lookups yet today.</TableCell></TableRow>}
              {data.per_user.map((u) => (
                <TableRow key={u.user_id} data-testid={`usage-row-${u.user_id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-xs">{u.role}</TableCell>
                  <TableCell className="text-xs">{u.usage.attom.used}/{u.usage.attom.cap}</TableCell>
                  <TableCell className="text-xs">{u.usage.skiptrace.used}/{u.usage.skiptrace.cap}</TableCell>
                  <TableCell>{u.disabled ? <Badge variant="destructive">Disabled</Badge> : <Badge variant="outline">Enabled</Badge>}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => override(u.user_id, "skiptrace")} data-testid={`override-${u.user_id}`}>+ Override</Button>
                      <Button size="sm" variant="ghost" className={u.disabled ? "text-emerald-600" : "text-destructive"} onClick={() => toggleAccess(u.user_id, !u.disabled)} data-testid={`toggle-access-${u.user_id}`}>
                        <Ban className="w-3.5 h-3.5 mr-1" />{u.disabled ? "Enable" : "Disable"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
